<template>
  <b-modal
    ref="discount-program-stack-modal"
    size="lg"
    hide-footer
    title="Chọn chương trình chiết khấu áp dụng cùng"
  >
    <div v-html="modalSubtitle" class="mb-3"></div>

    <div v-if="loading" class="text-center py-5 text-muted">
      <b-spinner small class="mr-2"></b-spinner> Đang tải chương trình...
    </div>

    <template v-else>
      <p v-if="mainProgramLabel" class="mb-2">
        Chương trình chính:
        <span
          class="label font-weight-bold label-inline label-light-primary"
          >{{ mainProgramLabel }}</span
        >
      </p>

      <b-table
        v-if="selectableList.length"
        class="table-bordered table-hover"
        :fields="fields"
        :items="selectableList"
        small
      >
        <template v-slot:cell(apply)="row">
          <b-form-checkbox
            :checked="isChecked(row.item.id)"
            :disabled="!isSelectable(row.item)"
            @change="toggle(row.item)"
          ></b-form-checkbox>
        </template>
        <template v-slot:cell(name)="row">
          <span>{{ row.item.name }}</span>
          <div
            v-if="!isSelectable(row.item) && !isChecked(row.item.id)"
            class="text-muted small"
          >
            Không stack được với chương trình đang chọn
          </div>
        </template>
        <template v-slot:cell(discount)="row">
          {{ formatProgramDiscount(row.item) }}
        </template>
      </b-table>

      <div v-else class="text-center py-4 text-muted">
        Không có chương trình nào để áp dụng thêm.
      </div>
    </template>

    <div class="d-flex justify-content-end mt-3">
      <b-button
        variant="primary"
        size="sm"
        class="mr-2"
        :disabled="loading"
        @click="onApply"
        >Áp dụng</b-button
      >
      <b-button variant="secondary" size="sm" @click="hideModal">Hủy</b-button>
    </div>
  </b-modal>
</template>

<script>
import ApiService from '@/core/services/api.service';
import { cmdUrl } from '@/utils/apiUrl';
import { convertPrice } from '@/utils/common';
import { AMOUNT_TYPE } from '@/utils/enum';
import { labelsFromDiscountProgramApi } from '@/utils/discountProgramDisplay';
import {
  parseExtraDiscountProgramIds,
  parseStackableProgramIds,
  canAddProgramToSelection,
} from '@/utils/discountProgramStack';

export default {
  props: {
    billItemId: [String, Number],
    productName: [String],
    productId: [String, Number],
    storeId: [String, Number],
    /** Program chính của dòng (từ by-product legacy) */
    mainProgramId: {
      type: [Number, String],
      default: null,
    },
    /** Id sản phẩm đi kèm (bundle) để truyền productIds khi tra cứu */
    attachProductIds: {
      type: Array,
      default: () => [],
    },
    /** CSV / mảng id program phụ đang chọn của dòng */
    selectedExtraIds: {
      type: [String, Array],
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      eligibleList: [],
      stackMapById: {},
      selectedIds: [],
      fields: [
        {
          key: 'apply',
          label: '',
          thStyle: { width: '8%', textAlign: 'center' },
          tdClass: 'text-center align-middle',
        },
        {
          key: 'name',
          label: 'Chương trình',
          thStyle: { fontWeight: 600, color: '#181c32', width: '62%' },
        },
        {
          key: 'discount',
          label: 'Chiết khấu',
          thStyle: { fontWeight: 600, color: '#181c32', width: '30%' },
          tdClass: 'text-right align-middle',
        },
      ],
    };
  },
  computed: {
    modalSubtitle() {
      return `<span style="font-size: 1rem">Sản phẩm: <a class="text-primary">#${
        this.productName || ''
      }</a></span>`;
    },
    normalizedMainId() {
      const n = Number(this.mainProgramId);
      return Number.isInteger(n) && n > 0 ? n : null;
    },
    mainProgramLabel() {
      if (!this.normalizedMainId) return '';
      const main = this.eligibleList.find(
        (item) => Number(item.id) === this.normalizedMainId,
      );
      if (main) {
        const { code, name } = labelsFromDiscountProgramApi(main);
        return name || code || `CK#${this.normalizedMainId}`;
      }
      return `CK#${this.normalizedMainId}`;
    },
    /** Danh sách program để chọn thêm: loại program chính */
    selectableList() {
      return this.eligibleList.filter(
        (item) => Number(item.id) !== this.normalizedMainId,
      );
    },
    /** Tập id cơ sở để kiểm tra pairwise: [main, ...extras đang chọn] */
    baseSelectionIds() {
      const ids = [...this.selectedIds];
      if (this.normalizedMainId) ids.unshift(this.normalizedMainId);
      return ids;
    },
  },
  methods: {
    convertPrice,
    isChecked(id) {
      return this.selectedIds.includes(Number(id));
    },
    /** Enable checkbox nếu thêm vào vẫn tạo tập pairwise hợp lệ (mục 4.3) */
    isSelectable(row) {
      const id = Number(row.id);
      if (this.selectedIds.includes(id)) return true;
      const baseWithoutCandidate = this.baseSelectionIds.filter(
        (x) => x !== id,
      );
      return canAddProgramToSelection(
        baseWithoutCandidate,
        id,
        this.stackMapById,
      );
    },
    toggle(row) {
      const id = Number(row.id);
      const idx = this.selectedIds.indexOf(id);
      if (idx >= 0) {
        this.selectedIds.splice(idx, 1);
      } else if (this.isSelectable(row)) {
        this.selectedIds.push(id);
      }
    },
    formatProgramDiscount(row) {
      const type = Number(row.discountType ?? AMOUNT_TYPE.MONEY);
      const amount = Number(row.discountAmount ?? 0);
      if (type === AMOUNT_TYPE.PERCENT) {
        const maxValue = Number(row.maxValue ?? 0);
        return maxValue > 0
          ? `${amount}% (tối đa ${convertPrice(maxValue)})`
          : `${amount}%`;
      }
      return convertPrice(amount);
    },
    async showModal() {
      this.selectedIds = parseExtraDiscountProgramIds(this.selectedExtraIds);
      this.eligibleList = [];
      this.stackMapById = {};
      this.loading = true;
      this.$refs['discount-program-stack-modal'].show();
      await this.fetchEligible();
    },
    hideModal() {
      this.$refs['discount-program-stack-modal'].hide();
    },
    async fetchEligible() {
      if (!this.productId || !this.storeId) {
        this.eligibleList = [];
        return;
      }
      this.loading = true;
      try {
        const productIds = [
          this.productId,
          ...this.attachProductIds.filter(Boolean),
        ];
        const { data } = await ApiService.query(
          `${cmdUrl.DiscountProgramUrl.byProduct}/${this.productId}/${this.storeId}`,
          { params: { includeStackable: true, productIds } },
        );
        if (data.status !== 1 || !Array.isArray(data.data)) {
          this.eligibleList = [];
          return;
        }
        this.eligibleList = data.data;
        await this.fetchStackMap();
      } catch (_) {
        this.eligibleList = [];
      } finally {
        this.loading = false;
      }
    },
    async fetchStackMap() {
      const ids = new Set();
      if (this.normalizedMainId) ids.add(this.normalizedMainId);
      this.eligibleList.forEach((item) => {
        const n = Number(item.id);
        if (Number.isInteger(n) && n > 0) ids.add(n);
      });
      const map = {};
      await Promise.all(
        [...ids].map(async (id) => {
          try {
            const endpoint = cmdUrl.DiscountProgramUrl.byId.replace('{id}', id);
            const { data } = await ApiService.get(endpoint);
            map[id] = parseStackableProgramIds(
              data && data.data ? data.data.stackableProgramIds : null,
            );
          } catch (_) {
            map[id] = [];
          }
        }),
      );
      this.stackMapById = map;
    },
    onApply() {
      const programs = this.selectableList.filter((item) =>
        this.selectedIds.includes(Number(item.id)),
      );
      const mainProgram =
        this.eligibleList.find(
          (item) => Number(item.id) === this.normalizedMainId,
        ) || null;
      this.$emit('apply', {
        billItemId: this.billItemId,
        extraIds: programs.map((item) => Number(item.id)),
        programs,
        mainProgram,
      });
      this.hideModal();
    },
  },
};
</script>
